import * as RadixProgressBar from "@radix-ui/react-progress";
import * as RadixSlider from "@radix-ui/react-slider";
import cx from "classnames";
import type { FC } from "react";

import {
	SIZE_CLASSNAME,
	THUMB_SIZE_CLASSNAME,
	THUMB_VARIANT_CLASSNAME,
	VALUE_LABEL_SIZE_CLASSNAME,
	VALUE_LABEL_VARIANT_CLASSNAME,
	VARIANT_CLASSNAME,
} from "./constants";
import { type ProgressBarSize, type ProgressBarVariant } from "./types";

export type ProgressBarProps = RadixProgressBar.ProgressProps & {
	label: string;
	size?: ProgressBarSize;
	variant?: ProgressBarVariant;
	onValueChange?: (value: number) => void;
	showValueLabel?: boolean;
};

const LOW_THRESHOLD = 33;
const MID_THRESHOLD = 66;

const isBetween = (value: number | null, min: number, max: number) =>
	value && value >= min && value <= max;
const isLowRange = (value: number | null) => isBetween(value, 0, LOW_THRESHOLD);
const isMidRange = (value: number | null) => isBetween(value, LOW_THRESHOLD + 1, MID_THRESHOLD);
const isHighRange = (value: number | null) => isBetween(value, MID_THRESHOLD + 1, Infinity);

export const ProgressBar: FC<ProgressBarProps> = ({
	className,
	label,
	max = 100,
	value = 0,
	size = "md",
	variant = "brand",
	showValueLabel = false,
	onValueChange,
	...props
}) => {
	const isEditable = typeof onValueChange === "function";
	const rootClassName = cx(
		"relative translate-z-0 rounded-full bg-translucent-tertiary w-full",
		SIZE_CLASSNAME[size],
		className
	);
	const variantClassName = cx(
		variant !== "graduated" && VARIANT_CLASSNAME[variant],
		variant === "graduated" && isLowRange(value) && VARIANT_CLASSNAME["negative"],
		variant === "graduated" && isMidRange(value) && VARIANT_CLASSNAME["warning"],
		variant === "graduated" && isHighRange(value) && VARIANT_CLASSNAME["positive"]
	);
	const rangeClassNames = cx("h-full", variantClassName, className);
	const thumbClassName = cx(
		"block transition-all outline-none focus:ring-4 hover:ring-4 active:ring-4 rounded-full",
		THUMB_SIZE_CLASSNAME[size],
		variant !== "graduated" && THUMB_VARIANT_CLASSNAME[variant],
		variant === "graduated" && isLowRange(value) && THUMB_VARIANT_CLASSNAME["negative"],
		variant === "graduated" && isMidRange(value) && THUMB_VARIANT_CLASSNAME["warning"],
		variant === "graduated" && isHighRange(value) && THUMB_VARIANT_CLASSNAME["positive"]
	);
	const valueLabelClassName = cx(
		"w-8",
		VALUE_LABEL_SIZE_CLASSNAME[size],
		variant !== "graduated" && VALUE_LABEL_VARIANT_CLASSNAME[variant],
		variant === "graduated" && isLowRange(value) && VALUE_LABEL_VARIANT_CLASSNAME["negative"],
		variant === "graduated" && isMidRange(value) && VALUE_LABEL_VARIANT_CLASSNAME["warning"],
		variant === "graduated" && isHighRange(value) && VALUE_LABEL_VARIANT_CLASSNAME["positive"]
	);

	const bar = isEditable ? (
		<RadixSlider.Root
			// Override child transitions to prevent thumb from lagging
			className={cx("flex items-center [&>*]:transition-none cursor-pointer", rootClassName)}
			defaultValue={[value ?? 0]}
			value={[value ?? 0]}
			max={max}
			step={1}
			onValueChange={(value) => {
				const actualValue = value[0];

				if (actualValue === undefined) {
					return;
				}

				onValueChange?.(actualValue);
			}}
		>
			<RadixSlider.Track className={cx("flex-1", SIZE_CLASSNAME[size])}>
				<RadixSlider.Range
					className={cx("absolute rounded-l-full transition-colors", rangeClassNames)}
				/>
			</RadixSlider.Track>
			<RadixSlider.Thumb className={thumbClassName} aria-label={label} />
		</RadixSlider.Root>
	) : (
		<RadixProgressBar.Root
			max={max}
			value={value}
			aria-label={label}
			{...props}
			className={cx("overflow-hidden", rootClassName)}
		>
			<RadixProgressBar.Indicator
				{...props}
				className={cx("rounded-full transition-all", rangeClassNames)}
				style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}
			/>
		</RadixProgressBar.Root>
	);

	return (
		<div className="flex w-full items-center gap-2">
			<div className="flex-1">{bar}</div>
			{showValueLabel && (
				<div className={valueLabelClassName}>{typeof value === "number" ? value : 0}%</div>
			)}
		</div>
	);
};
